import React from "react";

import dayjs from "dayjs";
import { observer } from "mobx-react-lite";

import { IconFileFolderFill } from "@synthesis/shared-icons";
import { Checkbox } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { useStores } from "../../../store";

type WrapperProps = {
  children: React.ReactNode;
};

const Wrapper = React.memo<WrapperProps>(({ children, ...rest }) => {
  return (
    <section className="mb-4" {...rest}>
      {children}
    </section>
  );
});

type FolderData = {
  id: string;
  baseName: string;
  path: string;
  createdAt: number;
  modifiedAt: number;
};

type FolderProps = {
  data: FolderData;
  onClick: (data: FolderData) => void;
  onContextMenu: (e: React.MouseEvent<HTMLDivElement>, data: FolderData) => void;
};

const CheckboxWrapper = ({ isActiveCheck, className, ...props }: React.HTMLAttributes<HTMLDivElement> & { isActiveCheck?: boolean }) => (
  <div className={cn("absolute -left-6 top-2 z-30 opacity-0 group-hover:opacity-100", isActiveCheck && "opacity-100", className)} {...props} />
);

const Container = ({ isActiveCheck, className, ...props }: React.HTMLAttributes<HTMLDivElement> & { isActiveCheck?: boolean }) => (
  <div
    className={cn(
      "hover:bg-neutrals-99 group relative flex cursor-pointer items-center gap-1 rounded-[14px] px-3 py-2 pb-2 transition-colors",
      isActiveCheck && "bg-tertiary-95 hover:bg-tertiary-95/75",
      className,
    )}
    {...props}
  />
);

const Folder = observer<FolderProps>(({ onClick, onContextMenu, data }) => {
  const { generalStore } = useStores();

  const onClickCheck = (folder: FolderData) => {
    generalStore.setCheckedContents({ id: folder.id, type: "DIRECTORY", size: 0 });
  };

  const isChecked = React.useMemo(() => generalStore.checkedFiles.get(data.id) !== undefined, [generalStore.checkedFiles, data]);

  return (
    <Container
      data-cy="listView-container-projectDocks"
      isActiveCheck={isChecked}
      onContextMenu={(e) => onContextMenu(e, data)}
      onClick={() => onClick(data)}
    >
      <div className="flex items-center">
        <IconFileFolderFill className="text-system-attention-50 size-4" />
      </div>
      <p className="text-type-common paragraph-sm px-2">{data.baseName}</p>

      <div className="ml-auto flex shrink-0 items-center gap-4">
        <p className="text-type-light paragraph-sm">{dayjs.unix(data.createdAt).format("DD.MM.YYYY HH:mm:ss")}</p>
        <p className="text-type-light paragraph-sm min-w-[140px]">{dayjs.unix(data.modifiedAt).format("DD.MM.YYYY HH:mm:ss")}</p>
      </div>

      <CheckboxWrapper isActiveCheck={isChecked} onClick={(e) => e.stopPropagation()}>
        <Checkbox onChange={() => onClickCheck(data)} checked={isChecked} />
      </CheckboxWrapper>
    </Container>
  );
});

const ListView = {
  Folder,
  Wrapper,
};

export default ListView;
